import { useState } from 'react';

import { ChevronsUpDown, Loader2 } from 'lucide-react';
import { Link, useParams } from 'react-router-dom';

import { useGetGames } from '@/api/getGames';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { getGameImage } from '@/hooks/useGameImage';
import { useGameSwitch } from '@/hooks/useGameSandboxSwitch';

interface GameBreadcrumbDropdownProps {
  gameCode: string;
  gameName: string;
}

const GameLogo = ({ code, name }: { code: string; name: string }) => (
  <img
    className="size-5 rounded-sm object-cover"
    src={getGameImage(code)}
    alt={name}
  />
);

export const GameBreadcrumbDropdown = ({
  gameCode,
  gameName,
}: GameBreadcrumbDropdownProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const isNonProduction = import.meta.env.MODE !== 'production';

  const { studioCode = '' } = useParams<{ studioCode: string }>();
  const { data: gamesData, isLoading } = useGetGames();
  // Switching Game stays inside the current Studio; the Studio crumb changes Studio.
  const studioGames =
    gamesData?.games.filter((game) => game.studio_code === studioCode) ?? [];

  const { switchGame } = useGameSwitch({
    onBeforeSwitch: () => {
      setIsOpen(false);
    },
  });

  const handleGameChange = (newGameCode: string) => {
    switchGame(studioCode, gameCode, newGameCode);
  };

  return (
    <>
      <span className="flex min-w-0 items-center gap-2">
        {/* The game's own pages: its Sandboxes are the section's landing page. */}
        <Link
          to={`/studios/${studioCode}/${gameCode}/sandboxes`}
          title="Manage game"
          className="flex min-w-0 items-center gap-2 hover:text-foreground transition-colors"
        >
          <GameLogo code={gameCode} name={gameName} />
          <span className="truncate">{gameName}</span>
        </Link>
        {isNonProduction && (
          <span
            className="shrink-0 text-xs data-[env=development]:text-cyan-300 data-[env=staging]:text-amber-300"
            data-env={import.meta.env.MODE}
          >
            {import.meta.env.MODE === 'development' ? 'Dev' : 'Staging'}
          </span>
        )}
      </span>
      <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="size-6"
            disabled={isLoading}
          >
            {isLoading ? (
              <Loader2 className="animate-spin" />
            ) : (
              <ChevronsUpDown />
            )}
            <span className="sr-only">Switch game</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-xs">
          <DropdownMenuRadioGroup
            value={gameCode}
            onValueChange={handleGameChange}
            className="max-h-48 overflow-y-auto"
          >
            {studioGames.map((game) => (
              <DropdownMenuRadioItem
                key={game.game_code}
                value={game.game_code}
              >
                <GameLogo code={game.game_code} name={game.name} />
                <span className="truncate">{game.name}</span>
              </DropdownMenuRadioItem>
            ))}
          </DropdownMenuRadioGroup>
        </DropdownMenuContent>
      </DropdownMenu>
    </>
  );
};
